import React, { useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import CarrierCard from './CarrierCard';
import CarrierPicker from './CarrierPicker';
import CarrierDetailsDialog from './CarrierDetailsDialog';
import LegAddButton from './LegAddButton';
import { carrierKey } from '../panel/utils';

// The subregion's Final Mile carriers, primary first. Auto-assign uses the primary; "+" makes another
// carrier primary. Every edit goes to the draft and is written on Save. Hovering a card reports its
// carrier through `onCarrierHover`, for the map.
const SubregionCarriersSection = ({
    draft,
    carriersForLeg,
    allCarriers = [],
    hoveredCarrierId = null,
    regionZipsByCarrier = {},
    onCarrierHover,
}) => {
    const [picking, setPicking] = useState(false);
    // A key, not the row, so the dialog shows the draft's latest copy.
    const [detailsKey, setDetailsKey] = useState(null);

    const mappings = draft.carriers;
    const options = useMemo(() => carriersForLeg('carrier_id'), [carriersForLeg]);
    // A saved carrier may no longer be eligible for the slot.
    const carrierOf = (mapping) =>
        options.find((c) => c.client_id === mapping.carrier_id) ||
        allCarriers.find((c) => c.client_id === mapping.carrier_id);
    const nameOf = (mapping) => carrierOf(mapping)?.business_name || 'Unknown carrier';
    const detailsMapping = mappings.find((m) => carrierKey(m) === detailsKey) || null;

    return (
        <Grid
            container
            direction="column"
            css={css`
                flex-wrap: nowrap;
                gap: 8px;
            `}
        >
            {mappings.map((mapping) => (
                <CarrierCard
                    key={carrierKey(mapping)}
                    name={nameOf(mapping)}
                    statuses={carrierOf(mapping)?.statuses}
                    onHoverChange={(hovering) => onCarrierHover?.(hovering ? mapping.carrier_id : null)}
                    // Only once its regions have loaded, and only while hovered.
                    noRegions={
                        hoveredCarrierId === mapping.carrier_id && regionZipsByCarrier[mapping.carrier_id]?.length === 0
                    }
                    disabled={draft.saving}
                    primary={!!mapping.is_primary}
                    onMakePrimary={mapping.is_primary ? null : () => draft.setPrimary(mapping)}
                    onRemove={() => draft.removeCarrier(mapping)}
                    onDetails={() => setDetailsKey(carrierKey(mapping))}
                />
            ))}
            {picking ? (
                <CarrierPicker
                    options={options}
                    mappings={mappings}
                    disabled={draft.saving}
                    onAdd={(carrier) => {
                        draft.addCarrier(carrier);
                        setPicking(false);
                    }}
                    onClose={() => setPicking(false)}
                />
            ) : (
                <LegAddButton onClick={() => setPicking(true)}>Add Carrier</LegAddButton>
            )}
            <CarrierDetailsDialog
                mapping={detailsMapping}
                name={detailsMapping ? nameOf(detailsMapping) : null}
                carriersForLeg={carriersForLeg}
                allCarriers={allCarriers}
                onChange={draft.updateCarrier}
                disabled={draft.saving}
                onClose={() => setDetailsKey(null)}
            />
        </Grid>
    );
};

export default SubregionCarriersSection;
